import type {
    CoordinateTO,
    FieldErrorTO,
    GeneratedRouteTO,
    HubTO,
    MissionEventTO,
    MissionProgressDetailsDataTO,
    MissionTO,
    PositionTO,
    ProgressDetailsFieldTO,
    ProgressDetailsValueTO,
    StateFieldTO,
    StateValueTO,
} from '@control-center/control-center-client-public-missions-ts';
import { z } from 'zod';

import type {
    MissionEventFE,
    MissionEventTypeFE,
    MissionFE,
    MissionHubFE,
    MissionProgressFE,
    MissionRouteFE,
    MissionStateFE,
} from '../mission';
import type { GeoPositionFE } from '../vehicle';
import {
    fromEnum,
    mapListPerEntry,
    safeField,
    safeOptional,
    sanitizeWire,
    toDate,
    toGeoPosition,
    toMonitoredField,
} from './restMapping';

/**
 * REST -> domain mapping for missions, served by
 * `@control-center/control-center-client-public-missions-ts` (`MissionsApi`):
 *
 * - `GET /api/v2/missions/active/{vin}`, `GET /api/v2/missions/{missionId}` and every
 *   `POST /api/v2/missions/{missionId}/{start|cancel|archive|reset}` return a `MissionTO`
 *   -> {@link missionFromRestV2}
 * - `GET /api/v2/missions` returns `MissionTO[]` -> {@link missionsFromRestV2}
 * - `GET /api/v2/missions/active/route/{vin}` returns a bare `GeneratedRouteTO`
 *   -> {@link missionRouteFromRestV2}
 *
 * Follows `vehicleFromRestV2.ts`, and is the first consumer of the shared helpers in
 * `restMapping.ts`. Mappers are the only modules allowed to see both a generated client's wire
 * shapes and `src/domain/`.
 */

const MISSION_STATE_PREFIX = 'MISSION_STATE_VALUE_';
const MISSION_EVENT_TYPE_PREFIX = 'MISSION_EVENT_TYPE_';

export const MISSION_STATES = [
    'ASSIGNED',
    'READY_TO_LAUNCH',
    'EXECUTING',
    'CANCELED',
    'FAILED',
    'PAUSED',
    'RESET',
    'COMPLETE',
    'LANDED',
    'PAUSING',
    'READY_TO_RESUME',
    'SCHEDULED',
    'UNKNOWN',
] as const satisfies readonly MissionStateFE[];

/** Exported for the same reason as {@link MISSION_STATES}. */
export const MISSION_EVENT_TYPES = [
    'MISSION_ARCHIVED',
    'MISSION_ASSIGNED',
    'MISSION_ASSIGNMENT_FAILED',
    'MISSION_CANCELED',
    'MISSION_COMPLETE',
    'MISSION_EXECUTING',
    'MISSION_LANDED',
    'MISSION_PAUSED_BY_MRM',
    'MISSION_PAUSING_FOR_MRM',
    'MISSION_READY_TO_LAUNCH',
    'MISSION_READY_TO_RESUME',
    'MISSION_RESET',
    'MISSION_RESUMED',
    'MISSION_SCHEDULED',
    'MISSION_VEHICLE_UNASSIGNED_REVERT_SCHEDULED',
    'UNKNOWN',
] as const satisfies readonly MissionEventTypeFE[];

const label = (field: string) => `missionFromRestV2.${field}`;

/** `{ latitude, longitude }`, the route polyline and hub form. */
const WireCoordinateSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
});

const WirePositionSchema = WireCoordinateSchema.extend({
    altitude_meters: z.number().optional(),
    heading_degrees: z.number().optional(),
});

/** `type` is widened from the wire enum so an unrecognised ERROR_TYPE_* folds to UNKNOWN. */
const WireFieldErrorSchema = z.object({
    type: z.string().optional(),
    updated_at: z.string().optional(),
});

/** `state` is widened from the wire enum so an unmodelled MISSION_STATE_VALUE_* folds to UNKNOWN. */
const WireStateValueSchema = z.object({
    state: z.string().optional(),
    updated_at: z.string().optional(),
});

const WireStateFieldSchema = z.object({
    ttl: z.number().optional(),
    value: WireStateValueSchema.optional(),
    error: WireFieldErrorSchema.optional(),
});

const WireProgressDataSchema = z.object({
    eta: z.string().optional(),
    remaining_distance_in_meters: z.number().optional(),
    traveled_distance_in_meters: z.number().optional(),
});

const WireProgressValueSchema = z.object({
    data: WireProgressDataSchema,
    updated_at: z.string().optional(),
});

const WireProgressDetailsFieldSchema = z.object({
    ttl: z.number().optional(),
    value: WireProgressValueSchema.optional(),
    error: WireFieldErrorSchema.optional(),
});

const WireHubSchema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    address: z.string().optional(),
    active: z.boolean().optional(),
    position: WireCoordinateSchema,
});

const WireMissionEventSchema = z.object({
    created_at: z.string().optional(),
    type: z.string().optional(),
    position: WirePositionSchema.optional(),
    repeat_count: z.number().optional(),
    last_repeated_at: z.string().optional(),
});

const WireMissionRouteObjectSchema = z.object({
    external_route_id: safeOptional(z.string(), label('route.external_route_id')),
    predefined_route_id: safeOptional(z.string(), label('route.predefined_route_id')),
    launch_hub: safeOptional(WireHubSchema, label('route.launch_hub')),
    landing_hub: safeOptional(WireHubSchema, label('route.landing_hub')),
    // Per-entry, so one unusable coordinate shortens the polyline instead of emptying it.
    positions: safeOptional(
        z.array(safeOptional(WireCoordinateSchema, label('route.positions[]'))),
        label('route.positions')
    ),
    total_distance_in_meters: safeOptional(z.number(), label('route.total_distance_in_meters')),
    duration_in_seconds: safeOptional(z.number(), label('route.duration_in_seconds')),
});

const WireMissionObjectSchema = z.object({
    // Identity. Strict on purpose: these must throw, see the module doc comment.
    mission_id: z.string().min(1),
    vin: z.string().min(1),
    fleet_id: safeOptional(z.string(), label('fleet_id')),
    name: safeOptional(z.string(), label('name')),
    description: safeOptional(z.string(), label('description')),
    timestamp: safeOptional(z.string(), label('timestamp')),
    actual_start_time: safeOptional(z.string(), label('actual_start_time')),
    actual_arrival_time: safeOptional(z.string(), label('actual_arrival_time')),
    planned_arrival_time: safeOptional(z.string(), label('planned_arrival_time')),
    // Per-entry, so one unparseable event does not erase the mission's history.
    events: safeOptional(z.array(safeOptional(WireMissionEventSchema, label('events[]'))), label('events')),
    route: safeOptional(WireMissionRouteObjectSchema, label('route')),
    state: safeField(WireStateFieldSchema, label('state')),
    progress_details: safeField(WireProgressDetailsFieldSchema, label('progress_details')),
});

export const WireMissionSchema = z.preprocess(sanitizeWire, WireMissionObjectSchema);

export const WireMissionRouteSchema = z.preprocess(sanitizeWire, WireMissionRouteObjectSchema);

type WireCoordinate = z.infer<typeof WireCoordinateSchema>;
type WirePosition = z.infer<typeof WirePositionSchema>;
type WireFieldError = z.infer<typeof WireFieldErrorSchema>;
type WireStateField = z.infer<typeof WireStateFieldSchema>;
type WireStateValue = z.infer<typeof WireStateValueSchema>;
type WireProgressDetailsField = z.infer<typeof WireProgressDetailsFieldSchema>;
type WireProgressValue = z.infer<typeof WireProgressValueSchema>;
type WireProgressData = z.infer<typeof WireProgressDataSchema>;
type WireHub = z.infer<typeof WireHubSchema>;
type WireMissionEvent = z.infer<typeof WireMissionEventSchema>;
type WireMissionObject = z.infer<typeof WireMissionObjectSchema>;
type WireRouteObject = z.infer<typeof WireMissionRouteObjectSchema>;

type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

export type MissionWireKeysExistOnSpec = {
    mission: KeysExistOn<WireMissionObject, MissionTO>;
    event: KeysExistOn<WireMissionEvent, MissionEventTO>;
    position: KeysExistOn<WirePosition, PositionTO>;
    state: KeysExistOn<WireStateField, StateFieldTO>;
    stateValue: KeysExistOn<WireStateValue, StateValueTO>;
    progressDetails: KeysExistOn<WireProgressDetailsField, ProgressDetailsFieldTO>;
    progressValue: KeysExistOn<WireProgressValue, ProgressDetailsValueTO>;
    progressData: KeysExistOn<WireProgressData, MissionProgressDetailsDataTO>;
    fieldError: KeysExistOn<WireFieldError, FieldErrorTO>;
};

export type MissionRouteWireKeysExistOnSpec = {
    route: KeysExistOn<WireRouteObject, GeneratedRouteTO>;
    hub: KeysExistOn<WireHub, HubTO>;
    coordinate: KeysExistOn<WireCoordinate, CoordinateTO>;
};

const toMissionHub = (raw: WireHub | null | undefined): MissionHubFE | undefined => {
    if (raw == null) {
        return;
    }
    const position = toGeoPosition(raw.position);
    if (position === undefined) {
        return;
    }
    return {
        id: raw.id ?? '',
        name: raw.name ?? '',
        address: raw.address ?? undefined,
        active: raw.active ?? false,
        position,
    };
};

const toMissionEvent = (raw: WireMissionEvent | null | undefined): MissionEventFE | undefined => {
    if (raw == null || (raw.created_at === undefined && raw.type === undefined)) {
        return;
    }
    const event: MissionEventFE = {
        type: fromEnum<MissionEventTypeFE>(raw.type, MISSION_EVENT_TYPE_PREFIX, MISSION_EVENT_TYPES),
        createdAt: toDate(raw.created_at) ?? null,
        lastRepeatedAt: toDate(raw.last_repeated_at) ?? null,
    };
    const position = toGeoPosition(raw.position);
    if (position !== undefined) {
        event.position = position;
    }
    if (typeof raw.repeat_count === 'number') {
        event.repeatCount = raw.repeat_count;
    }
    return event;
};

const toMissionProgress = (raw: WireProgressData | null | undefined): MissionProgressFE | undefined => {
    if (raw == null) {
        return;
    }
    return {
        eta: toDate(raw.eta) ?? null,
        remainingDistanceInMeters: raw.remaining_distance_in_meters ?? undefined,
        traveledDistanceInMeters: raw.traveled_distance_in_meters ?? undefined,
    };
};

const toMissionRoute = (raw: WireRouteObject): MissionRouteFE => ({
    externalRouteId: raw.external_route_id ?? undefined,
    predefinedRouteId: raw.predefined_route_id ?? undefined,
    launchHub: toMissionHub(raw.launch_hub),
    landingHub: toMissionHub(raw.landing_hub),
    positions: (raw.positions ?? []).reduce<GeoPositionFE[]>((acc, entry) => {
        const position = toGeoPosition(entry);
        if (position !== undefined) {
            acc.push(position);
        }
        return acc;
    }, []),
    totalDistanceInMeters: raw.total_distance_in_meters ?? undefined,
    durationInSeconds: raw.duration_in_seconds ?? undefined,
});

/** Maps one `MissionTO` body onto the domain {@link MissionFE}. Throws only on a missing identity. */
export const missionFromRestV2 = (raw: unknown): MissionFE => {
    const wire = WireMissionSchema.parse(raw);

    const events = (wire.events ?? []).reduce<MissionEventFE[]>((acc, entry) => {
        const event = toMissionEvent(entry);
        if (event !== undefined) {
            acc.push(event);
        }
        return acc;
    }, []);

    return {
        id: wire.mission_id,
        vin: wire.vin,
        fleetId: wire.fleet_id ?? undefined,
        name: wire.name ?? '',
        description: wire.description ?? undefined,
        timestamp: toDate(wire.timestamp) ?? null,
        actualStartTime: toDate(wire.actual_start_time) ?? null,
        actualArrivalTime: toDate(wire.actual_arrival_time) ?? null,
        plannedArrivalTime: toDate(wire.planned_arrival_time) ?? null,
        events,
        route: wire.route == null ? undefined : toMissionRoute(wire.route),
        state: toMonitoredField(
            wire.state,
            value => fromEnum<MissionStateFE>(value.state, MISSION_STATE_PREFIX, MISSION_STATES),
            value => value.updated_at
        ),
        progressDetails: toMonitoredField(
            wire.progress_details,
            value => toMissionProgress(value.data),
            value => value.updated_at
        ),
    };
};

/** Maps a bare `GeneratedRouteTO` body onto the domain {@link MissionRouteFE}. */
export const missionRouteFromRestV2 = (raw: unknown): MissionRouteFE =>
    toMissionRoute(WireMissionRouteSchema.parse(raw));

/**
 * Maps a `GET /api/v2/missions` body. Each entry is mapped on its own, so a mission whose identity
 * is unusable is dropped and logged while every other mission still reaches the UI. Use this
 * instead of `body.map(missionFromRestV2)`, which one bad entry would turn into a throw that
 * empties the whole list.
 */
export const missionsFromRestV2 = (raw: unknown): MissionFE[] => mapListPerEntry(raw, missionFromRestV2, label('list'));
